import moment from "moment-timezone";
import _ from "lodash";

import { OrderTotalByTypeResult } from "src/core/dashboard/api/widget-data.api";
import { OrderType } from "src/core/orders/api/orders.api";

import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import { OrderTotalByTypeGraphValue } from "../OrderTotalsByTypeWidget";
import { GraphDataType } from "../../WidgetTotalValueSwitcher";

/**
 * Helper to group order totals based on xAxisBy preference
 *
 * @param orderTotalsByType A list of flat order totals by type objects
 * @param displayRange DisplayRangeType
 * @param xAxisBy  "date" | "type"
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const orderTotalsByType = [
 *  {
      "type": "adhoc",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "type": "adhoc",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "type": "adhoc",
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1,
      "value": 8.509999871253967
    }
 * ]
 * const displayRange = "month"
 * const xAxisBy = "date"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "adhoc": 17.019999742507935
    },
    {
      "date": "2022-02-01T00:00:00.000Z",
      "adhoc": 8.509999871253967
    }
  ]
 * ```
 */
export const groupOrderTotalsByType = (orderTotalsByType: OrderTotalByTypeResult, xAxisBy: "date" | "type", displayRange?: DisplayRangeType, graphDataType?: GraphDataType): OrderTotalByTypeGraphValue[] => {
  const result: OrderTotalByTypeGraphValue[] = [];
  // Group the data based on displayRange
  const groupedOrderTypesData = xAxisBy === "date" && displayRange ? groupByDisplayRange(orderTotalsByType, displayRange) : _.groupBy(orderTotalsByType, "type");

  // Loop grouped data to form the result, ensuring to include xAxisBy property and all relevant values accumulated
  for (const xAxisGroup in groupedOrderTypesData) {
    if (xAxisBy === "date") {
      const accumulativeTotals = {
        adhoc: 0,
        bulk: 0,
        scheduled: 0
      };

      groupedOrderTypesData[ xAxisGroup ].forEach(row => {
        accumulativeTotals[ row.type ] += graphDataType === "total" ? row.count : row.value;
      });

      const dataItem: OrderTotalByTypeGraphValue = {
        date: moment.utc(xAxisGroup).toDate(),
        // Only include order types that have a value
        ..._.pickBy(accumulativeTotals, value => value !== 0)
      };

      // Force header to include date day if displayRange not by month
      if (displayRange !== "month") {
        dataItem.toolTipHeader = moment.utc(xAxisGroup).format("Do MMMM YYYY");
      }

      result.push(dataItem);
    } else {
      // accumulative total for xAxisBy type
      let accumulativeTotal = 0;

      groupedOrderTypesData[ xAxisGroup ].forEach(row => {
        accumulativeTotal += row.value;
      });

      const dataItem: OrderTotalByTypeGraphValue = {
        type: xAxisGroup as OrderType,
        value: accumulativeTotal,
        toolTipHeader: _.startCase(xAxisGroup.replace(/[_-]/g, " "))
      };

      result.push(dataItem);
    }
  }

  return result;
};
